import { RetailAccountCalculator } from "@component/RetailAccount/Calculator/RetailAccountCalculator";
import type { FAQSchema } from "@component/StructuredDataSchema";
import { StructuredDataSchema } from "@component/StructuredDataSchema";
import { YoutubeVideo } from "@component/Widget/YoutubeVideo/YoutubeVideo";
import { Config } from "@config";
import { 
	faMagnifyingGlass as fasMagnifyingGlass,
	faPaste as fasPaste,
	faPhone as fasPhone,
	faFileSignature as fasFileSignature
 } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BankLogosCloud } from "@landing/components/BankLogosCloud";
import { LandingAccordion } from "@landing/components/LandingAccordion";
import { LandingBottom } from "@landing/components/LandingBottom";
import { ExpertSlider } from "@ui/components/ExpertSlider";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { LandingContainer, LandingHowTo, LandingHowToItem } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Accordion, Col, Row } from "react-bootstrap";

/**
 * @Routing
 *
 * @title "Bankszámla kereső | Bankszámla nyitás"
 * @description "Hasonlítsd össze a bankok ajánlatait a bankszámla kalkulátorral, hogy megtaláld a legkedvezőbbet! Ingyenes bankszámla, számlavezetés, online számlanyitás."
 * @route "/bankszamla/"
 */
export default class Landing extends React.PureComponent {
	public render(): ReactNode {
		return (
			<>
				<div id="kalkulator">
					<RetailAccountCalculator />
				</div>
				<LandingContainer size="lg">
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Szakértőink, akik Neked segítenek</h2>
					<ExpertSlider groupName="BM_HQ_RETAIL_ACCOUNT" />
				</LandingContainer>
				<LandingContainer bgClassName="bg-gray">
					<LandingHowTo title="A bankszámla választás folyamata">
						<LandingHowToItem title="Keresés" icon={<FontAwesomeIcon icon={fasMagnifyingGlass} />}>
							Versenyeztesd meg a bankok ajánlatait, és válaszd a legjobbat.
						</LandingHowToItem>
						<LandingHowToItem title="Érdeklődés" icon={<FontAwesomeIcon icon={fasPaste} />}>
							Add le érdeklődésed a választott bankszámlára, és szakértőnk visszahív.
						</LandingHowToItem>
						<LandingHowToItem title="Visszahívás" icon={<FontAwesomeIcon icon={fasPhone} />}>
							Átbeszéljük a részleteket, megválaszoljuk a kérdéseid, segítünk a végleges döntésben.
						</LandingHowToItem>
						<LandingHowToItem title="Megnyitás" icon={<FontAwesomeIcon icon={fasFileSignature} />}>
							A szerződést a kiválasztott bankkal kötöd, ezt követően megnyitják a bankszámládat.
						</LandingHowToItem>
					</LandingHowTo>
				</LandingContainer>
				{this.renderFaq()}
				<LandingContainer>
					<BankLogosCloud
						variant="retailAccount"
						title="Bankszámla kalkulátorunk az alábbi bankok termékeit tartalmazza"
					/>
				</LandingContainer>
				{this.renderVideos()}
				<LandingBottom tags="bankszamla" />
				<StructuredDataSchema schema={this.getSchema()} />
			</>
		);
	}

	private renderVideos(): ReactNode {
		return (
			<LandingContainer as="aside">
				<div className="headline">
					<h2 className="h6 text-uppercase">Kapcsolódó videók</h2>
				</div>
				<Row className="justify-content-center">
					<Col md={6}>
						<YoutubeVideo
							videoId="uRVL0xoSAJI"
							title="Hogyan válassz bankszámlát?"
							description="Talán az egyik legtöbbet ismételt kérdés amit kapunk: Hogyan válasszunk bankszámlát? Ebben a videóban erre adunk választ."
							uploadDate="2023-01-20"
						/>
					</Col>
					<Col md={6}>
						<YoutubeVideo
							title="Bankszámla költségek - Tényleg drágák a bankszámlák?"
							videoId="HciP9g3fSlM"
							description="Sokat fizetünk a bankszámlánk után, erre jutott az MNB egy felmérés során. Átnézzük mire is költünk a számlánkhoz kapcsolódóan: mennyit fizetünk a számlavezetésért, a készpénzfelvételért, az átutalásért és az SMS értesítésekért. Adunk néhány tippet hogyan csökkenthetők jelentősen ezek a költség. Adott esetben akár egy teljesen díjmentes számlánk is lehet."
							uploadDate="2023-03-29"
						/>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private renderFaq(): ReactNode {
		return (
			<LandingContainer id="gyakori-kerdesek" sendEvent size="lg">
				<Row className="justify-content-center">
					<Col md="10">
						<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Gyakran ismételt kérdések</h2>
						<Accordion>
							<LandingAccordion title="Mire használhatjuk a bankszámlát?">
								<p>
									A bankszámlák a modern világ legalapvetőbb pénzügyi szolgáltatásai közé tartoznak.
									Segítségükkel megszabadulhatunk a készpénzhasználattal járó kényelmetlenségektől,
									helyette könnyedén és biztonságosan intézhetjük a napi pénzügyeinket. Bankszámlánkat
									használhatjuk arra, hogy <strong>megérkezzen rá a jövedelmünk</strong>, tárolhatjuk
									rajta a pénzünket, valamint kiadásainkat is intézhetjük a segítségével, akár{" "}
									<strong>bankkártyás fizetés</strong>, akár <strong>utalás</strong> formájában. A
									bankszámla elengedhetetlen akkor is, ha hitelt –{" "}
									<a href="/szemelyi-kolcson/?link_type=landing-faq">személyi kölcsön</a>,{" "}
									<a href="/lakashitel-kalkulator/?link_type=landing-faq">lakáshitel</a> vagy akár{" "}
									<a href="/babavaro-hitel/?link_type=landing-faq">Babaváró hitel</a> – vennénk fel.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen szolgáltatások kapcsolódnak egy bankszámlához?">
								<p>
									Bankszámlánkat elsősorban különféle pénzmozgások lebonyolítására, illetve a rajta
									elhelyezett pénzösszeg tárolására használhatjuk. A számlánkon lévő pénzből
									fizethetünk bankkártyával és online is, de indíthatunk utalásokat a netbankon
									keresztül, ahol számos egyéb funkció mellett például az egyenlegünk alakulását is
									követhetjük. A legtöbb bank már <strong>telefonos applikációt</strong> is
									kifejlesztett az ügyfelei számára, hogy a felhasználók még kényelmesebben
									intézhessék a pénzügyeiket. Van lehetőségünk készpénz befizetésére és felvételére
									is, ezenkívül léteznek még bankszámlához kapcsolható{" "}
									<strong>megtakarítási funkciók</strong> és hitelkeretek is.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Biztonságban van a bankszámlán tárolt pénzünk?">
								<p>
									A bankszámlán elhelyezett pénzünkre az Országos Betétbiztosítási Alap (OBA)
									garanciája vonatkozik, személyenként és bankonként 100 000 eurós összeghatárig,
									tehát nem kell tartanunk attól, hogy esetleg csődbe megy a számlavezető bankunk,
									mert <strong>ebben az esetben sem veszítenénk</strong> el a náluk lévő vagyonunkat.
									Nagyobb összegű megtakarítás esetén a pénzünket akár több banknál is el lehet
									helyezni, így az OBA által biztosított garancia megtöbbszörözhető.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Kinek érdemes bankszámlát nyitnia?">
								<p>
									Legalább egy bankszámla használata javasolt mindazoknak, akik rendszeres
									jövedelemmel rendelkeznek, akiknek hitelük van, akik sokat vásárolnak online, vagy
									akik egyszerűen nem szívesen foglalkoznak készpénzzel, hanem inkább haladnának a
									korral. Különösen jól jöhet egy bankszámla akkor is, amikor hitelt készülünk
									felvenni, ugyanis számos esetben kamatkedvezmény jár azoknak, akik a hitelnyújtó
									bank más szolgáltatásait (pl. számlavezetés) is igénybe veszik. Jó tudni azt is,
									hogy hitelfelvételnél nem minden bank fogadja el a készpénzes jövedelmet, ugyanis
									így nincs bankszámla, ami alapján ellenőrizhetik az adós költéseit, fizetési
									szokásait, beérkező jövedelmét.
									<br />
									<br />A bankszámlák vívmányainak köszönhetően nem kell többé sorban állnunk a postán
									vagy a bankban, hanem néhány pillanat alatt elintézhetjük pénzügyeinket a világ
									bármely pontjáról – aki szeretné kihasználni ezeket az előnyöket, nekik mindenképpen
									szükségük lesz egy bankszámlára.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Mely bankoknál nyithatunk bankszámlát?">
								<p>
									Bármely hazai pénzintézetnél van lehetőségünk bankszámlát nyitni, és egy személynek
									több banknál is lehet számlája egyszerre. A Bankmonitor bankszámla kalkulátorával a{" "}
									<a href="/cib/bankszamla/?link_type=landing-faq">CIB Bank</a>, az{" "}
									<a href="/erste/bankszamla/?link_type=landing-faq">Erste Bank</a>, a Gránit Bank, a{" "}
									<a href="/kh/bankszamla/?link_type=landing-faq">K&H Bank</a>, a MagNet Bank,{" "}
									<a href="/mbh/bankszamla/?link_type=landing-faq">MBH Bank</a>, a{" "}
									<a href="/raiffeisen/bankszamla/?link_type=landing-faq">Raiffeisen Bank</a>, az{" "}
									<a href="/unicredit/bankszamla/?link_type=landing-faq">UniCredit Bank</a> és az{" "}
									<a href="/otp/bankszamla/?link_type=landing-faq">OTP</a> bankszámlái közül segítünk
									kiválasztani az egyéni beállítások alapján a legjobbat.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Hogyan működik az online bankszámlanyitás?">
								<p>
									Ha jellemzően online intézed a banki ügyeket, akkor amellett, hogy a számlacsomag
									költségén is sokat faraghatsz, időt is spórolsz. A Bankmonitoron keresztül lehetőség
									van kényelmesen, otthonod kényelméből lakossági számlát nyitni. A szolgáltatás
									lehetővé teszi, hogy könnyen és gyorsan juss olcsóbb és jobb számlához.
								</p>
								<p> Az online bankszámlanyitás kétféleképpen történhet: </p>
								<ul>
									<li>
										Az első esetben a nap bármely szakában végigmehetsz a lépéseken (adatok
										megadása, igazolványok fotózása és feltöltése), majd ezt követően munkaidőben az
										ügyintéző felveszi veled a kapcsolatot és elbírálja a kérelmed. Ha minden
										rendben, csak egyetlen alkalommal kell a legközelebbi bankfiókba fáradni, előre
										egyeztetett időpontban, a szerződés aláírására.
									</li>
									<li>
										A második esetben a folyamat végig online történik: a valós idejű számlanyitásra
										videós azonosítással az adott pénzintézet nyitvatartási idejében kerülhet sor.
										Ebben az esetben tehát az otthonod kényelméből elintézhető az online
										bankszámlanyitás.
									</li>
								</ul>
								<p>
									A Bankmonitor bankszámla kalkulátorával ellenőrizheted, hogy melyik
									számlacsomagokhoz veheted igénybe az online számlanyitás szolgáltatást és el is
									indíthatod a folyamatot.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Hogyan választhatjuk ki a számunkra legjobb bankszámlát?">
								<p>
									Más számlacsomag való azoknak, akiknek rendszeres munkabér érkezik a számlájukra, és
									más azoknak, akiknek csak elvétve történik rajta jóváírás. Más akkor, ha valaki
									mindent kártyával és utalással fizet, és más, ha a teljes jövedelmét kiveszi egy
									automatából, majd minden kiadást készpénzben intéz. Az a legfontosabb, hogy ismerjük
									a szokásainkat, mert így biztosan meg fogjuk találni a számunkra{" "}
									<a href="/mediatar/cikk/6-szempont-ami-alapjan-erdemes-bankszamlat-valasztani/?link_type=landing-faq">
										legmegfelelőbb bankszámlát
									</a>
									. Ebben is rendelkezésünkre áll a Bankmonitor bankszámla kalkulátora.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen bankszámlatípusok vannak?">
								<p>
									A bankszámlák alapvetően két kategóriába sorolhatók: léteznek úgynevezett{" "}
									<strong>folyószámlák</strong>, amelyek a napi pénzügyek lebonyolítására használhatók
									és vannak <strong>betéti számlák</strong>, melyeken pénzösszegeket szokás tartani. A
									két kategóriának eltérő felhasználási módja és költségszerkezete van.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Mit jelent a prémium bankszámla?">
								<p>
									<a href="/raiffeisen-premium-bankszamla/?link_type=landing-faq">
										Prémium bankszámlát
									</a>{" "}
									a bankok általában a magasabb jövedelmű ügyfeleik számára kínálnak. Ennek keretein
									belül elérhetőek <strong>plusz szolgáltatások</strong> (például saját privát bankár
									állhat rendelkezésre), és gyakran még a díjak is kedvezőbbek a szimpla csomagokhoz
									képest. A prémium banki szolgáltatásokhoz általában 5–30 millió forintos
									megtakarítás vagy havi szinten legalább 400 ezer forintos jóváírás kell.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen költségei lehetnek egy bankszámlának?">
								<p>
									A leggyakrabban előforduló{" "}
									<a href="/mediatar/cikk/evi-76-ezer-forintot-sporoltunk-egy-olvasonk-bankszamlakoltsegein/?link_type=landing-faq">
										bankszámla-költségelemek
									</a>
									: havi számlavezetési díj, készpénzfelvételi díj, utalási díj, SMS-díj, kártyadíj.
									Ezeken felül léteznek még egyéb költségtípusok, melyek mértéke mindig az adott
									számlacsomagtól függ. Ha a szokásainkhoz leginkább passzoló számlát választjuk,
									akkor ideális esetben akár semelyik fenti költségtípusért nem kell fizetnünk, azaz a
									bankszámlánk{" "}
									<a href="/mediatar/cikk/valoban-ingyenes-egy-nulla-forintos-bankszamla/?link_type=landing-faq">
										teljesen ingyenes
									</a>{" "}
									is lehet.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Hogyan tudjuk összehasonlítani a bankszámlák költségeit?">
								<p>
									Az egyes számlacsomagok költség szerinti összehasonlítására érdemes a{" "}
									<a
										href="#kalkulator"
										onClick={() =>
											GoogleAnalytics4.sendEvent("BM_scrollTo", {
												buttonName: "faqToCalculator",
											})
										}
									>
										Bankmonitor online kalkulátorát
									</a>{" "}
									használni, melynek segítségével könnyedén megtalálhatjuk a saját szokásainkhoz
									legjobban illeszkedő ajánlatot. Fontos, hogy a bankszámla körültekintő kiválasztása
									önmagában még nem garancia a sikerre, hanem emellett a megfelelő módon is kell
									használnunk a szolgáltatást, hogy a bank ne terheljen minket extra költségekkel.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Létezik ingyenes bankszámla?">
								<p>
									Igen, a Bankmonitor kalkulátorával teljesen{" "}
									<a href="/bankszamla/ingyenes-bankszamla-online/?link_type=landing-faq">
										ingyenes bankszámlát
									</a>{" "}
									is elérhetünk, amennyiben teljesítjük az ehhez szükséges feltételeket. Egy átlagos
									bankszámla havonta több ezer forintba is kerülhet, ezért egy ingyenes bankszámla
									kiválasztása mindenki számára szemmel látható megtakarításokat eredményezhet.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen bankkártyát válasszunk a bankszámlához?">
								<p>
									A bankkártyáknak több típusa is létezik: a különböző verziókhoz más-más kiegészítő
									szolgáltatások kapcsolódhatnak (pl. utasbiztosítás), de természetesen a díjaik is
									másképpen alakulnak. A{" "}
									<a href="/mediatar/cikk/kell-e-neked-dombornyomott-kartya/?link_type=landing-faq">
										dombornyomott kártyákat
									</a>{" "}
									az a tévhit övezi, hogy kizárólag ezek alkalmasak internetes vásárlásra, illetve,
									hogy ezekhez külföldi utasbiztosítás is automatikusan jár. Az igazság az, hogy
									pusztán ezek miatt felesleges ilyen bankkártyát választani, ugyanis az{" "}
									<a href="/mediatar/cikk/dombornyomott-vagy-elektronikus-milyen-kartyat-valassz/?link_type=landing-faq">
										elektronikus bankkártyák
									</a>{" "}
									általában olcsóbbak, mégis éppúgy alkalmasak készpénzfelvételre és vásárlásra, mint
									a dombornyomott kártyák.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Mennyibe kerül a bankkártyás vásárlás?">
								<p>
									A bankkártyás vásárlás az ügyfél számára ingyenes, az elfogadó üzletnek kell
									bizonyos jutalékot fizetnie a bank felé.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Hogyan vehetünk fel ingyenesen készpénzt a bankszámlánkról?">
								<p>
									Havi két alkalommal, összesen 150 000 Ft összeghatárig{" "}
									<a href="/mediatar/cikk/evi-46-000-forintodba-is-kerulhet-ha-nem-ismered-az-ingyenes-keszpenzfelvetel-rejtelmeit/?link_type=landing-faq">
										díjmentesen vehetünk fel
									</a>{" "}
									készpénzt a bankszámlánkról, mégpedig bármelyik bank bankjegykiadó automatájából.
									Fontos azonban ha használni szeretnénk ezt a szolgáltatást, hogy erről{" "}
									<strong>nyilatkoznunk kell</strong> a számlavezető bankunknál. Számos banknál akár
									az internet- vagy netbankon keresztül is beállíthatjuk a jogszabályi díjmentes
									készpénzfelvételt A lehetőség személyenként egy bankszámlára él, vagyis, ha több
									lakossági folyószámlánk van, akkor el kell döntenünk, ezek közül melyikről vennénk
									fel díjmentesen készpénzt.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen gyakran érdemes felülvizsgálni a számlacsomagunkat?">
								<p>
									Mint minden más termék vagy szolgáltatás esetében, a bankszámláknál is folyamatosan
									jelennek meg új fejlesztések, amelyek kedvezőbbek lehetnek a korábbiaknál. Éppen
									ezért a szakértők azt javasolják, <strong>legalább kétévente ellenőrizzük</strong>,
									a számlánk megfelel-e a céljainknak, és nincs-e annál jobb lehetőség a piacon.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Hogyan szüntethetjük meg a bankszámlánkat?">
								<p>
									Ha már nem szeretnénk használni egy bankszámlát, érdemes mielőbb megszüntetnünk azt,
									különben az inaktivitásunk ideje alatt is felhalmozódhatnak költségek. Érdemes
									végiggondolnunk, maradt-e bármilyen kötelezettségünk a bankszámlánkhoz kapcsolódóan,
									ugyanis a megszüntetés csak akkor lehetséges, ha ezeket mind maradéktalanul
									rendeztük. <strong>Néha a megszüntetésnek is van költsége</strong>, erre is legyünk
									felkészülve. Bankkártyánkat minden esetben le kell adnunk a számla megszüntetésével
									egyidejűleg. A korábban erről a számláról induló állandó utalási megbízásokat
									(csoportos beszedési megbízásokat) is tartsuk észben: ezeket az új bankszámlánk
									terhére adjuk majd meg.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Hogyan válthatunk bankot a legegyszerűbben?">
								<p>
									Bankváltáskor számos teendő adódik: az új számlát meg kell nyitni, ha pedig a régire
									már nincs szükség, azt meg kell szüntetni. Intézkednünk kell jövedelmünk
									átirányításáról, gondoskodnunk kell csoportos beszedési és állandó átutalási
									megbízásainkról, hogy azok fennakadások nélkül tudjanak teljesülni. Ennek a
									bonyolult és időigényes folyamatnak a megkönnyítését célozza az{" "}
									<a href="/mediatar/cikk/eleged-van-ezert-bankot-valtanal-ilyen-egyszeru-a-dolog/?link_type=landing-faq">
										egyszerűsített bankváltás
									</a>
									. A folyamat célja, hogy mindent egy helyen intézhessünk, azaz ne kelljen régi
									bankunkat külön felkeresnünk. A bankváltást az újonnan választott banknál
									kezdeményezhetjük, a dolgot ettől kezdve a két bank intézi helyettünk.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen bankszámlát érdemes választaniuk nyugdíjasoknak?">
								<p>
									Egyre többen kapják{" "}
									<a href="/mediatar/cikk/milyen-bankszamlat-erdemes-nyitnia-egy-nyugdijasnak/?link_type=landing-faq">
										bankszámlára a nyugdíjukat
									</a>
									, ezért a kérdés igencsak aktuális. Nyugdíjasként kiemelten fontos, hogy megfelelő
									bankszámlát válasszunk: ahogyan idősödünk, egyre nehezebb a csekkek befizetése, a
									napi pénzügyek intézése. Egy bankszámla kényelmi szolgáltatásai sokat könnyíthetnek
									az életünkön, ugyanakkor nem mindegy, mennyit kell fizetnünk értük. A Bankmonitor
									kalkulátorában külön is foglalkozunk a nyugdíjasok számára elérhető legjobb
									számlacsomagokkal.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen bankszámlát érdemes választaniuk minimálbéreseknek?">
								<p>
									Minimálbéresek számára is rendkívül fontos a jó választás, hiszen alacsony jövedelem
									esetén minden költségre figyelni kell. Jó hír, hogy számos csomag feltételei akkor
									válnak kedvezőbbé, ha legalább a minimálbért elérő jóváírás megérkezik rá havonta,
									azaz{" "}
									<a
										href={`/bankszamla/?sort=FIRST_AND_SECOND_YEAR_TOTAL_FEE&receiptOfMoneyAmount=${Config.getMinWage()}&piecesCount=1&internetTransferCount=2&internetTransferAmount=150000&directDebitCount=0&directDebitAmount=0&cashWithdrawalOwnAtmAmount=50000&cardPurchaseCount=2&cardPurchaseAmount=15000&start=0&link_type=landing-faq`}
									>
										bankszámlát a minimálbéresek
									</a>{" "}
									akár ugyanazokkal a szolgáltatásokkal és díjszabásokkal élvezhetik, mint a magasabb
									jövedelműek.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Lehet-e több személynek közös bankszámlája?">
								<p>
									Egyes bankoknál és bizonyos feltételek mellett lehet{" "}
									<a href="/mediatar/cikk/kozos-tulajdonu-bankszamlat-keresel-van-ra-megoldas/?link_type=landing-faq">
										több tulajdonosa
									</a>{" "}
									egyazon bankszámlának, azonban az ezzel járó nehézségek miatt már vannak bankok,
									amelyek ezt teljesen kivezették a kínálatukból. Továbbra is létezik azonban a
									meghatalmazotti/rendelkezői státusz, amelyekkel a számlatulajdonos különböző
									jogosultságokat adhat az általa kiválasztott személyeknek. Társkártya igénylésére is
									van lehetőség, ami független attól, hogy a társkártya tulajdonosának van-e
									meghatalmazotti/rendelkezői felhatalmazása.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen módon adhatok utalási megbízást?">
								<p>
									Utalási megbízást háromféle formában is adhatunk: netbankon keresztül, mobilos
									applikációban, vagy személyesen valamelyik bankfiókban. Léteznek egyszeri, illetve
									állandó utalási megbízások, a kifizetni kívánt szolgáltatás jellegétől függően. Az
									utalási díjak jelentős részét képezhetik a banki költségeknek, ezért ha sokszor
									utalunk, érdemes olyan számlát választani, amelynél ez olcsó vagy akár ingyenes.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Mennyi idő alatt érkezik meg az elutalt pénz?">
								<p>
									2020. március 2-án élesedett Magyarországon az úgynevezett{" "}
									<a href="/mediatar/cikk/minden-amit-az-azonnali-fizetesi-rendszerrol-tudni-erdemes/?link_type=landing-faq">
										azonnali fizetési
									</a>{" "}
									rendszer, melynek köszönhetően a hét minden napján, 0-24 órában lehet egyszeri
									átutalásokat intézni, és ezek 5 másodpercen belül célba is érkeznek. A nagyszabású
									változtatással új időszámítás kezdődött a lakossági bankolás történetében, ami a
									banki költségeket is befolyásolhatja, ezért fontos, hogy a jövőben már csak emiatt
									is fokozott körültekintéssel válasszunk bankszámlát.
								</p>
							</LandingAccordion>
							<LandingAccordion title="Milyen hitelkeret tartozik a bankszámlánkhoz?">
								{" "}
								<p>
									Számos bankszámla mellé igényelhetünk hitelkeretet, melynek nagyságát általában a
									jövedelmünk szerint állapítják meg. A folyószámlahitel egyfajta biztonsági
									tartalékként is felfogható: amíg nem használjuk, addig nincs semmilyen költsége, de
									ha minden kötél szakad, akkor rendelkezésre áll. Nem rendeltetésszerű használat
									esetén ez a hiteltípus rendkívül drága lehet, ezért megfontolatlan túlköltekezésre
									semmiképpen nem javasolt.
								</p>
							</LandingAccordion>
						</Accordion>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private getSchema(): FAQSchema {
		return {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: [
				{
					"@type": "Question",
					name: "Létezik ingyenes bankszámla?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Igen, Magyarországon is léteznek ingyenes bankszámlák, amennyiben teljesítjük az ezekhez szükséges feltételeket.",
					},
				},
				{
					"@type": "Question",
					name: "Mennyibe kerül a bankkártyás vásárlás?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A bankkártyás vásárlás az ügyfél számára ingyenes, az elfogadó üzletnek kell bizonyos jutalékot fizetnie a bank felé.",
					},
				},
				{
					"@type": "Question",
					name: "Biztonságban van a pénzem a bankszámlán?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A bankszámlán elhelyezett pénzünkre az Országos Betétbiztosítási Alap (OBA) garanciája vonatkozik, személyenként és bankonként 100 000 eurós (kb. 36 millió forintos) összeghatárig. Nagyobb összegű megtakarítás esetén a pénzünket akár több banknál is el lehet helyezni, így az OBA által biztosított garancia megtöbbszörözhető.",
					},
				},
				{
					"@type": "Question",
					name: "Tudok anélkül bankszámlát nyitni, hogy be kelljen mennem a bankfiókba?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Igen, a legtöbb bank esetében már van lehetőség online számlanyitásra.",
					},
				},
				{
					"@type": "Question",
					name: "Hány éves kortól lehet valakinek bankszámlája?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Már 6-7 éves kortól is lehet bankszámlát nyitni a gyermekek számára, de a legtöbb pénzintézetnél 14 év az alsó korhatár.",
					},
				},
				{
					"@type": "Question",
					name: "Mennyi idő alatt érkezik meg az elutalt pénz?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "2020. március 2-án élesedett Magyarországon az úgynevezett azonnali fizetési rendszer, melynek köszönhetően a hét minden napján, 0-24 órában lehet egyszeri átutalásokat intézni, és ezek 5 másodpercen belül célba is érkeznek. ",
					},
				},
				{
					"@type": "Question",
					name: "Hogyan vehetek fel ingyenesen készpénzt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Havi két alkalommal, összesen 150 000 Ft összeghatárig díjmentesen vehetünk fel készpénzt a bankszámlánkról, mégpedig bármelyik bank bankjegykiadó automatájából. Fontos azonban, hogy erről nyilatkoznunk kell a számlavezető bankunknál, a lehetőség pedig személyenként egy bankszámlára él.",
					},
				},
				{
					"@type": "Question",
					name: "Milyen költségei lehetnek egy bankszámlának?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A leggyakrabban előforduló bankszámla-költségelemek: havi számlavezetési díj, készpénzfelvételi díj, utalási díj, SMS-díj, kártyadíj. Ezeken felül léteznek még egyéb költségtípusok, melyek mértéke mindig az adott számlacsomagtól függ.",
					},
				},
				{
					"@type": "Question",
					name: "Lehet-e több személynek közös bankszámlája?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Egyes bankoknál és bizonyos feltételek mellett lehet több tulajdonosa egyazon bankszámlának, azonban az ezzel járó nehézségek miatt már vannak bankok, amelyek ezt teljesen kivezették a kínálatukból.",
					},
				},
				{
					"@type": "Question",
					name: "Lehet egyszerre több bankszámlám?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Bármely hazai pénzintézetnél van lehetőségünk bankszámlát nyitni, egy személynek pedig több banknál is lehet számlája egyszerre.",
					},
				},
			],
		};
	}
}
